import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { BenefitsSummaryData, NetworkAmounts } from "../benefitsSummary.types";

const AmountColumn = ({
  label,
  value,
  met,
}: {
  label: string;
  value: string;
  met?: string;
}) => (
  <Grid size={{ xs: 6, md: 2 }}>
    {/* Column headers are shown once above the rows on desktop */}
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ display: { md: "none" } }}
    >
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
    {met && (
      <Typography variant="body2" color="text.secondary">
        {met}
      </Typography>
    )}
  </Grid>
);

const LimitRow = ({
  title,
  description,
  amounts,
}: {
  title: string;
  description: string;
  amounts: NetworkAmounts;
}) => (
  <Grid container columnSpacing={3} rowSpacing={1} sx={{ py: 2 }}>
    <Grid size={{ xs: 12, md: 8 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Grid>
    <AmountColumn
      label="In network"
      value={amounts.inNetwork}
      met={amounts.inNetworkMet}
    />
    <AmountColumn
      label="Out of network"
      value={amounts.outOfNetwork}
      met={amounts.outOfNetworkMet}
    />
  </Grid>
);

const PlanFact = ({
  label,
  value,
  description,
}: {
  label: string;
  value: string;
  description: string;
}) => (
  <Box
    sx={{
      bgcolor: "background.default",
      borderRadius: 1,
      p: 2,
      height: "100%",
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5 }}>
      {description}
    </Typography>
  </Box>
);

const PlanLimits = ({ data }: { data: BenefitsSummaryData }) => {
  const hasFamily = !!(data.familyDeductible || data.familyOutOfPocketMax);
  return (
    <Stack spacing={1}>
      <Grid
        container
        columnSpacing={3}
        sx={{ display: { xs: "none", md: "flex" } }}
      >
        <Grid size={8} />
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary">
            In network
          </Typography>
        </Grid>
        <Grid size={2}>
          <Typography variant="body2" color="text.secondary">
            Out of network
          </Typography>
        </Grid>
      </Grid>
      <Divider />
      <LimitRow
        title={
          hasFamily ? "Annual deductible, individual" : "Annual deductible"
        }
        description="What you pay before your plan starts sharing the cost of covered care. Some plans apply it before a copay, so your first bill can differ from the amounts above."
        amounts={data.deductible}
      />
      {data.familyDeductible && (
        <>
          <Divider />
          <LimitRow
            title="Annual deductible, family"
            description="Shared by everyone on the plan, so care for a sibling can count toward it."
            amounts={data.familyDeductible}
          />
        </>
      )}
      <Divider />
      <LimitRow
        title={
          hasFamily
            ? "Out-of-pocket maximum, individual"
            : "Out-of-pocket maximum"
        }
        description="The most your plan says you should pay in one plan year for covered care. Once you reach it, the plan pays covered costs for the rest of that year."
        amounts={data.outOfPocketMax}
      />
      {data.familyOutOfPocketMax && (
        <>
          <Divider />
          <LimitRow
            title="Out-of-pocket maximum, family"
            description="The most everyone on the plan together should pay in one plan year for covered care."
            amounts={data.familyOutOfPocketMax}
          />
        </>
      )}
      {data.outOfNetworkNote && (
        <>
          <Divider />
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            {data.outOfNetworkNote}
          </Typography>
        </>
      )}
      <Grid container spacing={2} sx={{ pt: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <PlanFact
            label="Plan year"
            value={data.planYear}
            description="The deductible and out-of-pocket maximum start over when a new plan year begins."
          />
        </Grid>
        {data.appliesTo && (
          <Grid size={{ xs: 12, md: 6 }}>
            <PlanFact
              label="Applies to"
              value={data.appliesTo}
              description={
                data.appliesTo === "Family"
                  ? "Family limits are shared by everyone on the plan, so care for a sibling can count toward them."
                  : "These limits apply only to your child's care."
              }
            />
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};

export default PlanLimits;
